/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Card, Checkbox, Col, Form, Input, Row, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

// Misc imports
import userHasEditAccess from '../../../../utils/misc/hasEditAccess';
import useTheme from '../../../../utils/hooks/useTheme';
import { isJson } from '../../../../utils/is/isJson';

// Constant declarations
const BotDeploymentsWebchatCustomSettingsForm = ({
  formData = {},
  userRoles = {},
  isCreateMode = false,
  form,
  isCustomSettingsEnabled,
  setIsCustomSettingsEnabled,
  hasErrors=0,
  shouldMarkAsErroneous
}) => {

  const { t } = useTranslation();
  const { formProps } = useTheme();
  const hasEditAccess = isCreateMode ? true : userHasEditAccess(formData?.tenant?.organizationId, userRoles?.organizationRoles, formData?.tenant.id, userRoles?.tenantRoles, true);
  useEffect(() => {
    if (!hasErrors) {
      shouldMarkAsErroneous(false);
      return;
    }
    if (
      form.getFieldError('customSettings').length > 0
    ) {
      shouldMarkAsErroneous(true);
      return;
    }
    shouldMarkAsErroneous(false);
  }, [hasErrors, form]);
  return (
    <Row gutter={formProps.rowGutter}>
      <Col xs={24}>
        <Typography.Text>
          {t('Custom Settings')}
        </Typography.Text>

        <Typography.Paragraph className="fs-sm" type="secondary">
          {t('Further customize your agent with any additional attributes in JSON.')}
        </Typography.Paragraph>
      </Col>

      <Col xs={24}>
        <Form.Item>
          <Row className="align-items-baseline">
            <Col flex="auto" xs={1}>
              <Checkbox onChange={(e) => {
                isCustomSettingsEnabled ? form.setFieldValue('customSettings', '') : form.setFieldValue('customSettings',
                  formData?.settings?.layout?.microsoftWebChat?.configuration?.customSettings
                    ? JSON.stringify(formData.settings.layout.microsoftWebChat.configuration.customSettings, null, '\t')
                    : '');
                setIsCustomSettingsEnabled(e.target.checked);
              }}
              checked={isCustomSettingsEnabled}
              disabled={!hasEditAccess} />
            </Col>

            <Col xs={23}>
              <Typography.Text className="ml-1">
                {t('Enable Custom Settings')}

                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('Add additional attributes for your agent in JSON format.')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Typography.Text>
            </Col>
          </Row>
        </Form.Item>

        <Form.Item shouldUpdate>
          {
            () => {
              return (
                <Card
                  size="small"
                  className="bg-layout-gray shadow-none"
                >
                  <Form.Item
                    validateFirst
                    name="customSettings"
                    hasFeedback
                    validateTrigger="onChange"
                    trigger="onChange"
                    shouldUpdate
                    className="mb-0"
                    rules={[
                      {
                        required: false,
                        validator: (rule, value) => {
                          if ((!isJson(value)) && isCustomSettingsEnabled) {
                            return Promise.reject();
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input.TextArea
                      disabled={!isCustomSettingsEnabled || !hasEditAccess}
                      placeholder={t('Custom JSON Input')}
                      autoSize={{ minRows: 6, maxRows: 32 }}
                    />
                  </Form.Item>
                </Card>
              );
            }
          }
        </Form.Item>
      </Col>
    </Row>
  );
};

BotDeploymentsWebchatCustomSettingsForm.propTypes = {
  formData: PropTypes.object,
  isCreateMode: PropTypes.bool,
  userRoles: PropTypes.object,
  form: PropTypes.object,
  isCustomSettingsEnabled: PropTypes.bool,
  setIsCustomSettingsEnabled: PropTypes.func,
  hasErrors: PropTypes.number,
  shouldMarkAsErroneous: PropTypes.func
};

export default BotDeploymentsWebchatCustomSettingsForm;
